import { useMemo } from "react";
import type {
  AuthoringIteration,
  AuthoringSampleScoreEntry,
  BreakdownRubric,
} from "../../lib/types";
import { BreakdownGrid } from "../../components/BreakdownGrid";
import { PromptBlock, PromptDiff } from "./PromptDiff";
import { formatTime, pluralize } from "./shared";

export function IterationCard({
  iteration,
  minScore,
  previousPrompt,
  previousLabel,
  hidePromptPanels = false,
  isBest = false,
}: {
  iteration: AuthoringIteration;
  minScore: number;
  previousPrompt: string | null;
  previousLabel: string;
  hidePromptPanels?: boolean;
  isBest?: boolean;
}) {
  const converged = iteration.fn === 0 && iteration.fp === 0;
  const scoresById = useMemo(() => {
    const map = new Map<string, AuthoringSampleScoreEntry>();
    for (const entry of iteration.scores) map.set(entry.contact_id, entry);
    return map;
  }, [iteration.scores]);
  return (
    <div className="contact" style={{ marginBottom: 12 }}>
      <div className="contact-top">
        <div>
          <div className="contact-name">Iteration {iteration.iteration}</div>
          <span className="card-desc">{formatTime(iteration.created_at)}</span>
        </div>
        <div className="chip-row">
          <span className="chip chip-on">
            Matched {iteration.tp} {pluralize(iteration.tp, "positive")}
          </span>
          <span className="chip chip-on">
            Rejected {iteration.tn} {pluralize(iteration.tn, "negative")}
          </span>
          <span className={`chip ${iteration.fn > 0 ? "chip-off" : "chip-on"}`}>
            Missed {iteration.fn} {pluralize(iteration.fn, "positive")}
          </span>
          <span className={`chip ${iteration.fp > 0 ? "chip-off" : "chip-on"}`}>
            Wrongly matched {iteration.fp} {pluralize(iteration.fp, "negative")}
          </span>
          {iteration.errors !== undefined && iteration.errors > 0 && (
            <span
              className="chip chip-off"
              title="Scoring API errors — these samples didn't score this iteration and weren't counted as misclassifications."
            >
              ⚠ {iteration.errors} scoring{" "}
              {pluralize(iteration.errors, "error")}
            </span>
          )}
          {converged && <span className="chip chip-on">converged</span>}
          {isBest && (
            <span
              className="chip chip-on"
              title="Lowest misclassification count — this prompt is the one promoted on Promote."
            >
              ★ best
            </span>
          )}
        </div>
      </div>

      {iteration.false_negatives.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>
            False negatives ({iteration.false_negatives.length}) — positives
            scored &lt; {minScore}
          </summary>
          <ul>
            {iteration.false_negatives.map((entry) => (
              <MisclassifiedEntry
                key={entry.contact_id}
                entry={entry}
                score={scoresById.get(entry.contact_id)}
              />
            ))}
          </ul>
        </details>
      )}

      {iteration.false_positives.length > 0 && (
        <details>
          <summary>
            False positives ({iteration.false_positives.length}) — negatives
            scored ≥ {minScore}
          </summary>
          <ul>
            {iteration.false_positives.map((entry) => (
              <MisclassifiedEntry
                key={entry.contact_id}
                entry={entry}
                score={scoresById.get(entry.contact_id)}
              />
            ))}
          </ul>
        </details>
      )}

      {!hidePromptPanels && (
        <>
          {previousPrompt !== iteration.prompt && (
            <PromptDiff
              previous={previousPrompt}
              current={iteration.prompt}
              previousLabel={previousLabel}
            />
          )}

          <details>
            <summary>
              Prompt at this iteration ({iteration.prompt.length} chars)
            </summary>
            <PromptBlock text={iteration.prompt} />
          </details>

          {iteration.breakdown_rubric &&
            Object.keys(iteration.breakdown_rubric).length > 0 && (
              <details>
                <summary>
                  Rubric at this iteration ({Object.keys(iteration.breakdown_rubric).length} axes)
                </summary>
                <RubricBlock rubric={iteration.breakdown_rubric} />
              </details>
            )}
        </>
      )}
    </div>
  );
}

function RubricBlock({ rubric }: { rubric: BreakdownRubric }) {
  return (
    <div className="breakdown" style={{ marginTop: 4 }}>
      {Object.entries(rubric).map(([axis, spec]) => {
        const detail =
          spec.values && spec.values.length > 0
            ? `one of ${spec.values.join(", ")}`
            : `${spec.min ?? "?"}..${spec.max ?? "?"}`;
        return (
          <span key={axis} style={{ display: "contents" }}>
            <span className="bd-label">{axis}</span>
            <span className="bd-value">{detail}</span>
          </span>
        );
      })}
    </div>
  );
}

function MisclassifiedEntry({
  entry,
  score,
}: {
  entry: { contact_id: string; domain: string; company: string; score: number | null };
  score: AuthoringSampleScoreEntry | undefined;
}) {
  const breakdown = score?.breakdown;
  const rationale = score?.rationale;
  return (
    <li style={{ marginBottom: 8 }}>
      <strong>{entry.company || entry.domain}</strong> — score {entry.score ?? "(error)"}
      <BreakdownGrid breakdown={breakdown} />
      {rationale && (
        <>
          <div className="rationale-label">Rationale</div>
          <div className="rationale">{rationale}</div>
        </>
      )}
    </li>
  );
}
